import * as React from 'react'
import { clsx } from 'clsx'
import type * as Polymorphic from '@radix-ui/react-polymorphic'

import textAlignStyles from '../../styles/atoms/text-align.module.css'
import iconSlotStyles from '../../styles/atoms/icon-slot.module.css'

import styles from './Heading.module.css'
import { useDeprecationWarning } from '../../hooks/useDeprecationWarning'

type HeadingOwnProps = {
  /**
   * Render as a different element, by default is inferred by size
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p'

  /**
   * Controls visual size of the heading
   * @default medium
   */
  size?:
    | 'xlarge'
    | 'large'
    | 'medium'
    | 'small'
    | 'xsmall'
    | 'xl'
    | 'lg'
    | 'md'
    | 'sm'
    | 'xs'

  /**
   * Align text to the left, center or right
   * @default left
   */
  align?: 'left' | 'center' | 'right'
  /**
   * Children to render inside the heading
   */
  children?: React.ReactNode
  /**
   * Additional CSS classes
   */
  className?: string
  /**
   * Prevent text from wrapping
   */
  truncate?: boolean | 'ellipsis'
  /**
   * Icon rendered beside the heading, centered on its first line.
   */
  icon?: React.ReactNode
  /**
   * Places the icon before or after the heading text.
   * @default start
   */
  iconPosition?: 'start' | 'end'
}

const ICON_ROW_JUSTIFY = {
  left: iconSlotStyles.justifyLeft,
  center: iconSlotStyles.justifyCenter,
  right: iconSlotStyles.justifyRight,
} as const

const levelBySize = {
  xlarge: 1,
  xl: 1,
  large: 2,
  lg: 2,
  medium: 3,
  md: 3,
  small: 4,
  sm: 4,
  xsmall: 5,
  xs: 5,
} as const

type PolymorphicHeading = Polymorphic.ForwardRefComponent<'h3', HeadingOwnProps>

/**
 * Semantic heading element
 */
// Ignoring this lint rule as we need to use the cast for the forwardRef to work
// on this polymorphic component
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const Heading = React.forwardRef(
  (
    {
      children,
      as,
      size = 'md',
      align = 'left',
      className,
      truncate,
      icon,
      iconPosition = 'start',
      ...headingProps
    }: HeadingOwnProps,
    forwardedRef,
  ) => {
    const Tag = as || `h${levelBySize[size]}`

    useDeprecationWarning(
      ['xlarge', 'large', 'medium', 'small', 'xsmall'].includes(size),
      `The size value is deprecated. Use the short version of it.`,
    )

    if (icon) {
      const iconSlot = <span className={iconSlotStyles.firstLine}>{icon}</span>
      return (
        <Tag
          className={clsx(
            styles.base,
            iconSlotStyles.row,
            styles.withIcon,
            styles[size],
            ICON_ROW_JUSTIFY[align],
            className,
          )}
          ref={forwardedRef}
          {...headingProps}
        >
          {iconPosition === 'start' && iconSlot}
          <span
            className={clsx(iconSlotStyles.label, truncate && styles.truncate)}
          >
            {children}
          </span>
          {iconPosition === 'end' && iconSlot}
        </Tag>
      )
    }

    return (
      <Tag
        className={clsx(
          styles.base,
          styles[size],
          textAlignStyles[align],
          truncate && styles.truncate,
          className,
        )}
        ref={forwardedRef}
        {...headingProps}
      >
        {children}
      </Tag>
    )
  },
) as PolymorphicHeading

Heading.displayName = 'Heading'

export { Heading }
export type { HeadingOwnProps as HeadingProps }
